'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { saveDailyResponse } from '@/lib/services/dailyResponseService';
import { createClient } from '@/lib/supabase/client';
import { allDaysContent, DayContent, DayScenario, DayRitual } from './daysData';

/* ─────────────────────────────────────────────
   EAT Utilities
───────────────────────────────────────────── */
function getEATNow(): Date {
  const now = new Date();
  const eatOffsetMs = 3 * 60 * 60 * 1000;
  return new Date(now.getTime() + eatOffsetMs - now.getTimezoneOffset() * 60 * 1000);
}

function getEATDateString(date?: Date): string {
  const d = date || getEATNow();
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function msUntilEATMidnight(): number {
  const eatNow = getEATNow();
  const nextMidnightEAT = new Date(
    Date.UTC(eatNow.getUTCFullYear(), eatNow.getUTCMonth(), eatNow.getUTCDate() + 1, 0, 0, 0)
  );
  const eatOffsetMs = 3 * 60 * 60 * 1000;
  const nowUTC = new Date();
  return nextMidnightEAT.getTime() - eatOffsetMs - nowUTC.getTime() + nowUTC.getTimezoneOffset() * 60 * 1000;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function checkDayUnlocked(startDateStr: string, dayNumber: number): boolean {
  const todayStr = getEATDateString();
  const start = new Date(startDateStr + 'T00:00:00Z');
  const today = new Date(todayStr + 'T00:00:00Z');
  const elapsed = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  // Day N unlocks when elapsed >= N-1
  return elapsed >= dayNumber - 1;
}

/* ─────────────────────────────────────────────
   JOURNAL MODAL
───────────────────────────────────────────── */
interface JournalModalProps {
  title: string;
  promptText: string;
  onClose: () => void;
  onSave: (entry: string, name: string) => Promise<void>;
  saving: boolean;
  saved: boolean;
}

function JournalModal({ title, promptText, onClose, onSave, saving, saved }: JournalModalProps) {
  const [entry, setEntry] = useState('');
  const [name, setName] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-secondary px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Icon name="PencilSquareIcon" size={18} className="text-white" />
            </div>
            <h3 className="text-white font-headline font-bold text-base">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <Icon name="XMarkIcon" size={16} className="text-white" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-primary/8 border border-primary/20 rounded-xl p-4">
            <p className="text-sm text-foreground font-body italic leading-relaxed">{promptText}</p>
          </div>
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-1.5">
              Your Journal Entry <span className="text-red-500">*</span>
            </label>
            <textarea
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              rows={5}
              placeholder="Write your honest reflection here…"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-1.5">
              Your Name <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Leave blank to submit as Anonymous"
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
            />
          </div>
          {saved && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <Icon name="CheckCircleIcon" size={18} className="text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 font-body font-medium">
                Your response has been saved! Keep going — you are doing great.
              </p>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button
              onClick={() => onSave(entry, name)}
              disabled={saving || !entry.trim()}
              className="flex-1 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Icon name="CheckIcon" size={16} className="text-primary-foreground" />
                  Save Response
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-border text-foreground rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:bg-muted"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
interface DayExperienceSectionProps {
  dayNumber: number;
}

const DayExperienceSection: React.FC<DayExperienceSectionProps> = ({ dayNumber }) => {
  const dayContent: DayContent | undefined = allDaysContent[dayNumber];

  const [sectionOpen, setSectionOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'scenarios' | 'rituals'>('scenarios');
  const [openScenario, setOpenScenario] = useState<number | null>(null);
  const [expandedRitual, setExpandedRitual] = useState<number | null>(null);

  const [journalModal, setJournalModal] = useState<{
    open: boolean;
    title: string;
    promptText: string;
    responseType: 'scenario' | 'ritual';
    scenarioId?: number;
    scenarioContext?: string;
    ritualTime?: string;
  } | null>(null);
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);

  const [completedScenarios, setCompletedScenarios] = useState<Set<number>>(new Set());
  const [completedRituals, setCompletedRituals] = useState<Set<string>>(new Set());

  const [isDayUnlocked, setIsDayUnlocked] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [unlockCountdown, setUnlockCountdown] = useState('');
  // Day status: 'today' | 'past' | 'unknown'
  const [dayStatus, setDayStatus] = useState<'today' | 'past' | 'unknown'>('unknown');

  useEffect(() => {
    setIsHydrated(true);

    const loadUnlockState = async () => {
      try {
        const savedEmail = typeof window !== 'undefined' ? localStorage.getItem('calendar_email') : null;
        if (savedEmail) {
          const supabase = createClient();
          const { data } = await supabase
            .from('calendar_progress')
            .select('journey_start_date')
            .eq('email', savedEmail)
            .maybeSingle();

          if (data?.journey_start_date) {
            const unlocked = checkDayUnlocked(data.journey_start_date, dayNumber);
            setIsDayUnlocked(unlocked);
            if (unlocked) {
              // Compute elapsed to determine if this is today or past
              const todayStr = getEATDateString();
              const start = new Date(data.journey_start_date + 'T00:00:00Z');
              const today = new Date(todayStr + 'T00:00:00Z');
              const elapsed = Math.max(0, Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
              // Day N is "today" when elapsed === dayNumber - 1
              setDayStatus(elapsed === dayNumber - 1 ? 'today' : 'past');
            }
            return;
          }
        }
        setIsDayUnlocked(false);
      } catch {
        setIsDayUnlocked(false);
      }
    };

    loadUnlockState();
  }, [dayNumber]);

  // Countdown timer
  useEffect(() => {
    if (isDayUnlocked) return;
    const tick = () => {
      const ms = msUntilEATMidnight();
      setUnlockCountdown(formatCountdown(ms));
      if (ms <= 0) setIsDayUnlocked(true);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isDayUnlocked]);

  // Auto-unlock at midnight
  useEffect(() => {
    if (isDayUnlocked) return;
    const ms = msUntilEATMidnight();
    const timeout = setTimeout(() => setIsDayUnlocked(true), ms);
    return () => clearTimeout(timeout);
  }, [isDayUnlocked]);

  const openJournalForScenario = (scenario: DayScenario) => {
    setJournalSaved(false);
    setJournalModal({
      open: true,
      title: `Day ${dayNumber} · ${scenario.context}`,
      promptText: scenario.action,
      responseType: 'scenario',
      scenarioId: scenario.id,
      scenarioContext: scenario.context,
    });
  };

  const openJournalForRitual = (ritual: DayRitual) => {
    setJournalSaved(false);
    setJournalModal({
      open: true,
      title: `${ritual.emoji} ${ritual.time} Ritual · ${ritual.title}`,
      promptText: `${ritual.description}\n\nSteps:\n${ritual.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}`,
      responseType: 'ritual',
      ritualTime: ritual.time,
    });
  };

  const handleSaveJournal = async (entry: string, name: string) => {
    if (!journalModal || !entry.trim()) return;
    setJournalSaving(true);
    try {
      await saveDailyResponse({
        participant_name: name || 'Anonymous',
        response_text: entry,
        response_type: journalModal.responseType,
        scenario_id: journalModal.scenarioId,
        scenario_context: journalModal.scenarioContext,
        ritual_time: journalModal.ritualTime,
        day_number: dayNumber,
      });
      setJournalSaved(true);
      if (journalModal.responseType === 'scenario' && journalModal.scenarioId !== undefined) {
        setCompletedScenarios((prev) => new Set([...prev, journalModal.scenarioId!]));
      } else if (journalModal.responseType === 'ritual' && journalModal.ritualTime) {
        setCompletedRituals((prev) => new Set([...prev, journalModal.ritualTime!]));
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setJournalSaving(false);
    }
  };

  if (!isHydrated || !dayContent) return null;

  const weekColors: Record<number, { badge: string; border: string; bg: string; text: string }> = {
    2: { badge: 'bg-rose-100 text-rose-700 border-rose-200', border: 'border-rose-200', bg: 'from-rose-50 to-pink-50', text: 'text-rose-700' },
    3: { badge: 'bg-teal-100 text-teal-700 border-teal-200', border: 'border-teal-200', bg: 'from-teal-50 to-emerald-50', text: 'text-teal-700' },
    4: { badge: 'bg-amber-100 text-amber-700 border-amber-200', border: 'border-amber-200', bg: 'from-amber-50 to-yellow-50', text: 'text-amber-700' },
  };
  const colors = weekColors[dayContent.week] || weekColors[2];

  // ── Locked state ──
  if (!isDayUnlocked) {
    return null;
  }

  // ── Unlocked state ──
  const scenarios = dayContent.scenarios;
  const rituals = dayContent.rituals;
  const completedCount = completedScenarios.size + completedRituals.size;
  const totalCount = scenarios.length + rituals.length;

  return (
    <section className={`py-10 lg:py-14 bg-gradient-to-br ${colors.bg}`}>
      {journalModal?.open && (
        <JournalModal
          title={journalModal.title}
          promptText={journalModal.promptText}
          onClose={() => setJournalModal(null)}
          onSave={handleSaveJournal}
          saving={journalSaving}
          saved={journalSaved}
        />
      )}

      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className={`rounded-2xl border-2 ${colors.border} bg-white/80 overflow-hidden`}>
          <button
            onClick={() => setSectionOpen(!sectionOpen)}
            className="w-full text-left"
          >
            <div className={`bg-gradient-to-br ${colors.bg} px-6 py-5 flex items-center justify-between gap-4`}>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-foreground font-headline font-bold text-xl">{dayNumber}</span>
                </div>
                <div>
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-cta font-bold border mb-1 ${colors.badge}`}>
                    Week {dayContent.week} · {dayContent.weekTheme}
                  </div>
                  <h2 className="text-xl lg:text-2xl font-headline font-bold text-foreground">
                    Day {dayNumber}: {dayContent.title}
                  </h2>
                  <p className={`text-sm font-body ${colors.text}`}>{dayContent.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="hidden sm:flex items-center gap-2">
                  <span className="text-xs font-cta font-bold text-muted-foreground">
                    {completedCount}/{totalCount} completed
                  </span>
                  {dayStatus === 'today' && (
                    <span className="flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-cta font-bold">
                      <Icon name="CheckCircleIcon" size={14} className="text-green-600" />
                      Today
                    </span>
                  )}
                  {dayStatus === 'past' && (
                    <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-cta font-bold">
                      <Icon name="CheckCircleIcon" size={14} className="text-slate-500" />
                      Completed
                    </span>
                  )}
                </div>
                <Icon
                  name={sectionOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={22}
                  className="text-primary"
                />
              </div>
            </div>
          </button>

          {sectionOpen && (
            <div className="p-6 space-y-6">
              {/* Objective */}
              <div className="bg-primary/5 rounded-xl p-5 border border-primary/10">
                <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-2">Objective</p>
                <p className="text-sm text-foreground font-body leading-relaxed">{dayContent.objective}</p>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 border-b border-border pb-0">
                {(['scenarios', 'rituals'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-5 py-2.5 rounded-t-lg font-cta text-sm font-bold transition-all duration-200 border-b-2 ${
                      activeTab === tab
                        ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tab === 'scenarios' ? `${scenarios.length} Life Scenarios` : `${rituals.length} Daily Rituals`}
                  </button>
                ))}
              </div>

              {/* Scenarios Tab */}
              {activeTab === 'scenarios' && (
                <div className="space-y-3">
                  <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider">
                    {dayContent.howTo}
                  </p>
                  {scenarios.map((scenario) => (
                    <div key={scenario.id} className="rounded-xl border border-primary/15 overflow-hidden bg-white/70">
                      <button
                        onClick={() => setOpenScenario(openScenario === scenario.id ? null : scenario.id)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-primary/5 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name={scenario.icon as any} size={16} className="text-primary" />
                          </div>
                          <span className="font-cta font-bold text-foreground text-sm">{scenario.context}</span>
                          {completedScenarios.has(scenario.id) && (
                            <Icon name="CheckCircleIcon" size={16} className="text-green-500" />
                          )}
                        </div>
                        <Icon
                          name={openScenario === scenario.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                          size={18}
                          className="text-primary flex-shrink-0"
                        />
                      </button>

                      {openScenario === scenario.id && (
                        <div className="px-5 pb-5 space-y-4 border-t border-primary/10">
                          <div className="pt-4">
                            <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-2">Reflection Questions</p>
                            <ul className="space-y-1.5">
                              {scenario.reflection.map((q, qi) => (
                                <li key={qi} className="flex items-start gap-2 text-sm text-foreground font-body">
                                  <span className="text-primary mt-0.5 flex-shrink-0">→</span>
                                  <span>{q}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="bg-primary/5 rounded-lg p-4">
                            <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-1">Example</p>
                            <p className="text-sm text-foreground font-body italic">{scenario.example}</p>
                          </div>
                          <div>
                            <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider mb-1">Common Emotional Response</p>
                            <p className="text-sm text-foreground font-body">{scenario.emotionalResponse}</p>
                          </div>
                          <div className="bg-secondary/10 rounded-lg p-4 border-l-4 border-secondary">
                            <p className="text-xs font-cta font-bold text-secondary uppercase tracking-wider mb-1">Insight</p>
                            <p className="text-sm text-foreground font-body leading-relaxed">{scenario.insight}</p>
                          </div>
                          <div className="bg-transformation-gold/10 rounded-lg p-4 border border-transformation-gold/30">
                            <p className="text-xs font-cta font-bold text-transformation-gold uppercase tracking-wider mb-1">✍ Journaling Action</p>
                            <p className="text-sm text-foreground font-body leading-relaxed">{scenario.action}</p>
                          </div>
                          <button
                            onClick={() => openJournalForScenario(scenario)}
                            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md flex items-center justify-center gap-2"
                          >
                            <Icon name="PencilSquareIcon" size={16} className="text-primary-foreground" />
                            Record My Response
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Rituals Tab */}
              {activeTab === 'rituals' && (
                <div className="space-y-4">
                  {rituals.map((ritual, idx) => (
                    <div key={ritual.time} className={`rounded-xl border ${ritual.borderColor} overflow-hidden`}>
                      <button
                        onClick={() => setExpandedRitual(expandedRitual === idx ? null : idx)}
                        className={`w-full flex items-center justify-between px-5 py-4 ${ritual.bgColor} hover:opacity-90 transition-opacity text-left`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${ritual.iconBg} flex items-center justify-center flex-shrink-0`}>
                            <span className="text-xl">{ritual.emoji}</span>
                          </div>
                          <div>
                            <p className={`text-xs font-cta font-bold uppercase tracking-wider ${ritual.color}`}>{ritual.time} · {ritual.duration}</p>
                            <p className="font-headline font-bold text-foreground text-sm">{ritual.title}</p>
                          </div>
                          {completedRituals.has(ritual.time) && (
                            <Icon name="CheckCircleIcon" size={16} className="text-green-500" />
                          )}
                        </div>
                        <Icon
                          name={expandedRitual === idx ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                          size={18}
                          className={ritual.color}
                        />
                      </button>

                      {expandedRitual === idx && (
                        <div className="px-5 pb-5 space-y-4 bg-white/60">
                          <p className="text-sm text-foreground font-body leading-relaxed pt-4">{ritual.description}</p>
                          <div>
                            <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider mb-2">Steps</p>
                            <ol className="space-y-2">
                              {ritual.steps.map((step, si) => (
                                <li key={si} className="flex items-start gap-3 text-sm text-foreground font-body">
                                  <span className={`w-6 h-6 rounded-full ${ritual.iconBg} flex items-center justify-center flex-shrink-0 text-xs font-bold ${ritual.color}`}>
                                    {si + 1}
                                  </span>
                                  <span>{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                          <button
                            onClick={() => openJournalForRitual(ritual)}
                            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md flex items-center justify-center gap-2"
                          >
                            <Icon name="PencilSquareIcon" size={16} className="text-primary-foreground" />
                            Record My Practice
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DayExperienceSection;
